/* Depth videos: the active card plays muted while the section is on screen; clicking it opens the
   video with sound. Image cards (no video set) keep the original enlarge behaviour. */
if (!customElements.get('trapt-detail-gallery')) {
  customElements.define('trapt-detail-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const on = (target, name, handler, options = {}) => target.addEventListener(name, handler, { signal: this.abort.signal, ...options });
      this.track = this.querySelector('.t762-details__track');
      this.items = [...this.querySelectorAll('.t762-details__item')];
      this.dialog = this.querySelector('dialog');
      this.fullImage = this.dialog.querySelector('img');
      this.fullVideo = this.dialog.querySelector('video');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.fine = matchMedia('(hover: hover) and (pointer: fine)');
      this.index = 0;
      this.inView = false;
      if (!this.items.length) return;
      this.querySelector('.t762-details__controls').hidden = false;
      const previous = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      on(previous, 'click', () => this.go(this.index - 1));
      on(next, 'click', () => this.go(this.index + 1));
      on(this.track, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const target = event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.go(target);
      });
      on(this.track, 'scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const positions = this.positions();
          /* <= so the later card wins when several share the same clamped position (the end of the track). */
          const nearest = positions.reduce((best, value, i) => Math.abs(value - this.track.scrollLeft) <= Math.abs(positions[best] - this.track.scrollLeft) ? i : best, 0);
          this.select(nearest);
        });
      });
      this.items.forEach((item, index) => {
        const card = this.card(item);
        on(card, 'click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (index !== this.index) { this.go(index); return; }
          this.open(card);
        });
        on(card, 'pointermove', event => {
          if (index !== this.index || this.reduced.matches || !this.fine.matches) return;
          const box = item.getBoundingClientRect();
          card.style.setProperty('--tilt-x', ((.5 - (event.clientY - box.top) / box.height) * 4).toFixed(2) + 'deg');
          card.style.setProperty('--tilt-y', (((event.clientX - box.left) / box.width - .5) * 4).toFixed(2) + 'deg');
        });
        on(card, 'pointerleave', () => this.resetTilt(card));
      });
      on(this.querySelector('.t762-details__close'), 'click', () => this.dialog.close());
      on(this.dialog, 'click', event => { if (event.target === this.dialog) this.dialog.close(); });
      on(this.dialog, 'close', () => {
        this.fullVideo.pause();
        this.fullVideo.removeAttribute('src');
        this.fullVideo.load();
        this.restoreScroll();
        this.opener?.focus({ preventScroll: true });
        this.syncPlayback();
      });
      on(document, 'shopify:block:select', event => {
        const index = this.items.findIndex(item => item === event.target || item.contains(event.target));
        if (index >= 0) this.go(index);
      });
      on(document, 'visibilitychange', () => this.syncPlayback());
      this.resize = new ResizeObserver(() => {
        this.track.scrollTo({ left: this.positions()[this.index], behavior: 'instant' });
      });
      this.resize.observe(this.track);
      this.viewport = new IntersectionObserver(entries => {
        this.inView = entries.some(entry => entry.isIntersecting);
        this.syncPlayback();
      }, { threshold: 0.35 });
      this.viewport.observe(this.track);
      this.select(0, true);
    }
    card(item) {
      return item.querySelector('.t762-details__card');
    }
    open(card) {
      const video = card.dataset.video;
      if (video) {
        this.fullImage.hidden = true;
        this.fullVideo.hidden = false;
        this.fullVideo.poster = card.dataset.poster || '';
        this.fullVideo.src = video;
        this.fullVideo.setAttribute('aria-label', card.dataset.label || 'TRAPT video');
      } else {
        this.fullVideo.hidden = true;
        this.fullImage.hidden = false;
        this.fullImage.src = card.href;
        this.fullImage.alt = card.dataset.label || '';
      }
      this.opener = card;
      this.pauseAll();
      this.dialog.showModal();
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      if (video) this.fullVideo.play().catch(() => {});
      this.querySelector('.t762-details__close').focus();
    }
    pauseAll() {
      this.items.forEach(item => item.querySelector('.t762-details__video')?.pause());
    }
    syncPlayback() {
      const canPlay = this.inView && !document.hidden && !this.dialog.open && !this.reduced.matches;
      this.items.forEach((item, i) => {
        const video = item.querySelector('.t762-details__video');
        if (!video) return;
        if (canPlay && i === this.index) {
          if (video.preload !== 'auto') video.preload = 'auto';
          video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }
    positions() {
      /* Clamped to how far the track can actually scroll, so the last cards stay reachable
         now that there is no viewport-wide spacer after them. */
      const first = this.items[0].offsetLeft;
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      return this.items.map(item => Math.min(item.offsetLeft - first, max));
    }
    go(index) {
      index = Math.max(0, Math.min(this.items.length - 1, index));
      this.track.scrollTo({ left: this.positions()[index], behavior: this.reduced.matches ? 'instant' : 'smooth' });
    }
    resetTilt(card) {
      card.style.removeProperty('--tilt-x');
      card.style.removeProperty('--tilt-y');
    }
    restoreScroll() {
      if (this.savedOverflow === undefined) return;
      document.documentElement.style.overflow = this.savedOverflow;
      this.savedOverflow = undefined;
    }
    select(index, initial = false) {
      if (!initial && this.index === index) return;
      this.index = index;
      this.items.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        item.classList.toggle('is-before', i < index);
        const card = this.card(item);
        const label = card.dataset.label || '';
        const action = card.dataset.video ? 'Play with sound: ' : 'Enlarge: ';
        card.setAttribute('aria-label', (i === index ? action : 'Show: ') + label);
        this.resetTilt(card);
      });
      this.querySelector('[data-current]').textContent = String(index + 1).padStart(2, '0');
      this.querySelector('[data-prev]').disabled = index === 0;
      this.querySelector('[data-next]').disabled = index === this.items.length - 1;
      this.style.setProperty('--detail-progress', ((index + 1) / this.items.length * 100) + '%');
      if (!initial) this.querySelector('[role="status"]').textContent = 'Video ' + (index + 1) + ' of ' + this.items.length;
      this.syncPlayback();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.viewport?.disconnect();
      cancelAnimationFrame(this.frame);
      this.pauseAll();
      if (this.dialog?.open) this.dialog.close();
      this.restoreScroll();
    }
  });
}
